// Microsoft variant of the Strategic Primary Selection judgment row. Shows the LLM verdict
// on goal-set coherence with a collapsible "Goals Claude saw" table beneath. Microsoft has
// no per-goal primary/secondary distinction, so the table emphasizes the
// Contributing-vs-Excluded split (the MS equivalent question) plus tracking status.

import type { EvidenceProps } from './registry';

interface GoalRow {
  id?: string;
  name?: string;
  type?: string;
  category?: string;
  scope?: string;
  status?: string;
  excludeFromBidding: boolean;
  trackingStatus?: string;
  attributionModelType?: string;
}

interface StrategicMsShape {
  totalGoals: number;
  contributingCount: number;
  excludedCount: number;
  firingCount: number;
  goals: GoalRow[];
}

function formatTracking(t: string | undefined): string {
  if (!t) return '—';
  if (t === 'RecordingConversions') return 'Firing';
  if (t === 'NoRecentConversions') return 'Dormant';
  if (t === 'NotTracking') return 'Not tracking';
  return t;
}

export const StrategicPrimaryMicrosoftEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as StrategicMsShape;

  return (
    <div className='evidence'>
      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Goals Claude saw
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.totalGoals} total · {e.contributingCount} contributing · {e.firingCount} firing
          </span>
        </h4>
        <table className='evidence__table evidence__table--strategic-ms'>
          <thead>
            <tr>
              <th>Goal name</th>
              <th>Type</th>
              <th>Status</th>
              <th>Role</th>
              <th>Tracking</th>
              <th>Scope</th>
            </tr>
          </thead>
          <tbody>
            {e.goals.map(r => (
              <tr key={r.id ?? r.name}>
                <td>
                  <div>{r.name ?? '(unnamed)'}</div>
                  {r.id && <div className='evidence__action-id'>{r.id}</div>}
                </td>
                <td>{r.type ?? '—'}</td>
                <td>{r.status ?? '—'}</td>
                <td>
                  <span
                    className={`evidence__pill ${
                      r.excludeFromBidding
                        ? 'evidence__pill--excluded'
                        : 'evidence__pill--in-metric'
                    }`}
                  >
                    {r.excludeFromBidding ? 'Excluded' : 'Contributing'}
                  </span>
                </td>
                <td>{formatTracking(r.trackingStatus)}</td>
                <td>{r.scope ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
};
